import { useState } from "react";
import { UserMinus } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PermissionButton } from "@/components/ui/permission-button";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateItemMutation } from "@/services/(company)/payrollRunsApi";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

interface IProps {
  runId: number;
  itemId: number;
  onExcluded?: () => void;
}

/**
 * Take one employee out of this run, with the reason kept on the item (B8).
 * The API accepted `excludedReason`; no screen sent it. A failed employee in
 * review has no other way past (retry is draft-only).
 */
export const ExcludeItemButton = ({ runId, itemId, onExcluded }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [updateItem, { isLoading }] = useUpdateItemMutation();

  const submit = async () => {
    try {
      await updateItem({
        runId,
        itemId,
        // The same words are the exclusion reason and the audited edit reason.
        data: { excludedReason: reason.trim(), reason: reason.trim() },
      }).unwrap();
      toast.success(t("company:payrolls.employee_excluded"));
      setOpen(false);
      setReason("");
      onExcluded?.();
    } catch (err: unknown) {
      const msg = (err as { data?: { message?: unknown } })?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : String(msg ?? t("company:payrolls.action_failed")),
      );
    }
  };

  return (
    <>
      <PermissionButton
        section={PLATFORM_SECTION_ENUM.PAYROLL}
        action="edit"
        mode="disable"
        variant="outline-danger"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen(true)}
      >
        <UserMinus size={14} />
        {t("company:payrolls.exclude_from_run")}
      </PermissionButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("company:payrolls.exclude_from_run_title")}
            </DialogTitle>
            <DialogDescription>
              {t("company:payrolls.exclude_from_run_desc")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("company:payrolls.exclude_reason_placeholder")}
            aria-label={t("company:payrolls.exclude_reason_placeholder")}
            rows={3}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("common:buttons.cancel")}
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={reason.trim().length < 3 || isLoading}
              onClick={submit}
            >
              {t("company:payrolls.exclude_from_run")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
